import {
  AssessmentEntry,
  AssessmentMethodType,
  AssessmentsGrade,
  AssessmentWithEntries,
  isAssessmentEntryComment,
  isAssessmentEntryGrade,
  isAssessmentEntryScore,
} from '@schooly/api';
import { createContext, FC, PropsWithChildren, useCallback, useContext } from 'react';

import { AnalyticsEventName, trackAmplitudeEvent } from './index';

type AssessmentAnalyticsEntryUpdateParams = {
  entry?: AssessmentEntry;
  update: {
    studentId: string;
    methodId: string;
    entry: AssessmentEntry | null;
    groupId: string;
    methodType: AssessmentMethodType;
  };
  assessment: AssessmentWithEntries;
};

type AssessmentAnalyticsContextValue = {
  trackAssessmentEntryUpdate: (params: AssessmentAnalyticsEntryUpdateParams) => void;
  trackAssessmentOpenComment: (params: {
    assessment: AssessmentWithEntries;
    groupId: string;
  }) => void;
} | null;

const AssessmentAnalyticsContext = createContext<AssessmentAnalyticsContextValue>(null);

type AssessmentAnalyticsProviderProps = PropsWithChildren<{
  source: 'report' | 'studentProfile';
  grades: AssessmentsGrade[];
}>;

export const AssessmentAnalyticsProvider: FC<AssessmentAnalyticsProviderProps> = ({
  children,
  source,
  grades,
}) => {
  const getMethodValue = useCallback(
    (entry: AssessmentEntry) => {
      if (isAssessmentEntryScore(entry)) {
        return entry.score;
      }
      if (isAssessmentEntryGrade(entry)) {
        return grades.flatMap((g) => g.options).find((o) => o.id === entry.select_list_option_id)
          ?.name;
      }

      if (isAssessmentEntryComment(entry)) {
        return entry.comment;
      }

      return null;
    },
    [grades],
  );

  const trackAssessmentEntryUpdate = useCallback(
    ({ assessment, update, entry }: AssessmentAnalyticsEntryUpdateParams) => {
      const methodValue = entry ? String(getMethodValue(entry)) : null;
      const updatedMethodValue = update.entry ? String(getMethodValue(update.entry)) : null;

      if (!methodValue && !updatedMethodValue) return;

      const subjectName = assessment.groups.find((g) => g.id === update.groupId)?.subject?.name;

      if (update.methodType === AssessmentMethodType.Comment) {
        trackAmplitudeEvent({
          name: AnalyticsEventName.AssessmentEditComment,
          payload: {
            subject_name: subjectName,
            char_count: updatedMethodValue?.length,
            aol: assessment.area_of_learning?.name ? [assessment.area_of_learning.name] : undefined,
          },
        });
      }

      if (source === 'studentProfile') {
        trackAmplitudeEvent({
          name: AnalyticsEventName.AssessmentEditStudentAssessments,
          payload: {
            student_id: update.studentId,
            assessment_id: assessment.id,
            action: !methodValue ? 'add' : 'edit',
          },
        });
      }

      if (source === 'report') {
        trackAmplitudeEvent({
          name: AnalyticsEventName.ReportEditScoreGrade,
          payload: {
            subject_name: subjectName,
            new_value: updatedMethodValue ?? '',
            previous_value: methodValue ?? '',
            aol: assessment.area_of_learning?.name ? [assessment.area_of_learning.name] : undefined,
          },
        });
      }
    },
    [getMethodValue, source],
  );

  const trackAssessmentOpenComment = useCallback(
    ({ assessment, groupId }: { assessment: AssessmentWithEntries; groupId: string }) => {
      const subjectName = assessment.groups.find((g) => g.id === groupId)?.subject?.name;
      trackAmplitudeEvent({
        name: AnalyticsEventName.AssessmentOpenComment,
        payload: {
          assessment_date: assessment.assessment_date,
          subject_name: subjectName,
          aol: assessment.area_of_learning?.name ? [assessment.area_of_learning.name] : undefined,
        },
      });
    },
    [],
  );

  return (
    <AssessmentAnalyticsContext.Provider
      value={{ trackAssessmentEntryUpdate, trackAssessmentOpenComment }}
    >
      {children}
    </AssessmentAnalyticsContext.Provider>
  );
};

export const useAssessmentAnalytics = () => {
  return useContext(AssessmentAnalyticsContext);
};
